import { Fragment } from "react"
import {
  Block,
  LabelMedium,
  LabelSmall,
  ParagraphSmall,
  Radio,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { useField } from "formik"

import { useEstimateVeBTCValue } from "#/shared/hooks"
import { STAKING_LOCK_DURATION, StakingLockDurationType } from "#/shared/types"
import { bigIntToHumanReadableFormat, stakingUtils } from "#/shared/utils"

import StakingItemContainer from "../StakingItemContainer"

export type FieldLockDurationProps = {
  fieldName: string
  value?: StakingLockDurationType
  setValue?: (value: StakingLockDurationType) => void
  isDisabled?: boolean
  currentDuration?: StakingLockDurationType
  stakeAmount: bigint
}

function LockDurationOption({
  duration,
  currentDuration,
  stakeAmount,
  isDisabled,
  value,
  setValue,
  fieldName,
}: FieldLockDurationProps & { duration: StakingLockDurationType }) {
  const [, theme] = useStyletron()
  const [_, meta, helpers] = useField<StakingLockDurationType>(fieldName)

  const isCurrentDuration = duration === currentDuration

  const label = `${stakingUtils.getLockDurationLabel(duration)} ${
    isCurrentDuration ? "(current duration)" : ""
  }`

  const veBTC = useEstimateVeBTCValue(stakeAmount, duration)

  return (
    <StakingItemContainer>
      <Block>
        <LabelMedium>{label}</LabelMedium>

        {stakeAmount !== 0n && (
          <ParagraphSmall as="span" color={theme.colors.contentSecondary}>
            You’ll receive
            <LabelSmall
              as="span"
              color={theme.colors.contentSecondary}
            >{` ${bigIntToHumanReadableFormat(veBTC)} veBTC`}</LabelSmall>
          </ParagraphSmall>
        )}
      </Block>
      <Radio
        value={duration.toString()}
        // TODO: Add tooltip when button is disabled
        disabled={isDisabled}
        checked={duration === (value ?? meta.value)}
        onChange={(event) => {
          const newValue = Number(event.target.value)

          if (typeof setValue === "function") {
            setValue(newValue)
          } else {
            helpers.setValue(newValue)
          }
        }}
      />
    </StakingItemContainer>
  )
}

export default function FieldLockDuration(props: FieldLockDurationProps) {
  const [css, theme] = useStyletron()

  return (
    <Block
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: theme.sizing.scale300,
      })}
    >
      <LabelMedium>Lock duration</LabelMedium>
      <Block
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: theme.sizing.scale100,
        })}
      >
        {(
          Object.values(
            STAKING_LOCK_DURATION,
          ) as unknown as StakingLockDurationType[]
        )
          .reverse()
          .map((lockDuration) => (
            <Fragment key={lockDuration}>
              <LockDurationOption duration={lockDuration} {...props} />
            </Fragment>
          ))}
      </Block>
    </Block>
  )
}
